export const ASSET_USAGE_STATUS = Object.freeze({
  NEW: 'new',
  CORE: 'core',
  ACTIVE: 'active',
  COOLING: 'cooling',
  DORMANT: 'dormant',
});

export const ASSET_USAGE_STATUS_LABELS = {
  [ASSET_USAGE_STATUS.NEW]: '新規',
  [ASSET_USAGE_STATUS.CORE]: '中核',
  [ASSET_USAGE_STATUS.ACTIVE]: '使用中',
  [ASSET_USAGE_STATUS.COOLING]: '見直し',
  [ASSET_USAGE_STATUS.DORMANT]: '休眠',
};

export const ASSET_USAGE_RULE_VERSION = 1;

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const NEW_ASSET_DAYS_THRESHOLD = 14;
const CORE_REFERENCES_LAST_30_DAYS_THRESHOLD = 5;
const CORE_LAST_REFERENCE_DAYS_THRESHOLD = 14;
const ACTIVE_REFERENCES_LAST_30_DAYS_THRESHOLD = 2;
const ACTIVE_LAST_REFERENCE_DAYS_THRESHOLD = 30;
const COOLING_REFERENCE_COUNT_THRESHOLD = 3;
const COOLING_LAST_REFERENCE_DAYS_THRESHOLD = 90;
const ARCHIVE_CANDIDATE_LAST_REFERENCE_DAYS_THRESHOLD = 90;
const ARCHIVE_CANDIDATE_CREATED_DAYS_THRESHOLD = 30;

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function diffDays(fromDate, toDateValue = new Date()) {
  if (!fromDate) return null;
  return Math.max(0, Math.floor((toDateValue.getTime() - fromDate.getTime()) / DAY_IN_MS));
}

export function deriveAssetUsageStatus({
  referenceCount = 0,
  referencesLast30Days = 0,
  daysSinceLastReference = null,
  createdDaysAgo = 0,
}) {
  const safeDaysSinceLastReference = typeof daysSinceLastReference === 'number' ? daysSinceLastReference : Number.POSITIVE_INFINITY;
  const normalizedReferenceCount = Math.max(0, Number(referenceCount) || 0);
  const normalizedReferencesLast30Days = Math.max(0, Number(referencesLast30Days) || 0);
  const normalizedCreatedDaysAgo = Math.max(0, Number(createdDaysAgo) || 0);

  if (normalizedReferenceCount === 0) {
    return normalizedCreatedDaysAgo <= NEW_ASSET_DAYS_THRESHOLD ? ASSET_USAGE_STATUS.NEW : ASSET_USAGE_STATUS.DORMANT;
  }

  if (
    normalizedReferencesLast30Days >= CORE_REFERENCES_LAST_30_DAYS_THRESHOLD &&
    safeDaysSinceLastReference <= CORE_LAST_REFERENCE_DAYS_THRESHOLD
  ) {
    return ASSET_USAGE_STATUS.CORE;
  }

  if (
    normalizedReferencesLast30Days >= ACTIVE_REFERENCES_LAST_30_DAYS_THRESHOLD &&
    safeDaysSinceLastReference <= ACTIVE_LAST_REFERENCE_DAYS_THRESHOLD
  ) {
    return ASSET_USAGE_STATUS.ACTIVE;
  }

  if (
    normalizedReferenceCount >= COOLING_REFERENCE_COUNT_THRESHOLD &&
    safeDaysSinceLastReference <= COOLING_LAST_REFERENCE_DAYS_THRESHOLD
  ) {
    return ASSET_USAGE_STATUS.COOLING;
  }

  return ASSET_USAGE_STATUS.DORMANT;
}

export function deriveAssetUsageMetrics(asset = {}, now = new Date()) {
  const metadata = asset?.metadata || {};
  const referenceCount = Math.max(0, Number(metadata.referenceCount ?? asset.referenceCount ?? 0) || 0);
  const lastReferencedAt = toDate(metadata.lastReferencedAt ?? asset.lastReferencedAt);
  const createdAt = toDate(asset.createdAt);
  const daysSinceLastReference = diffDays(lastReferencedAt, now);
  const createdDaysAgo = diffDays(createdAt, now) ?? 0;

  let referencesLast30Days = Number(metadata.referencesLast30Days ?? asset.referencesLast30Days);
  if (!Number.isFinite(referencesLast30Days)) {
    if (referenceCount === 0) {
      referencesLast30Days = 0;
    } else if (typeof daysSinceLastReference === 'number' && daysSinceLastReference <= 30) {
      referencesLast30Days = 1;
    } else {
      referencesLast30Days = 0;
    }
  }
  referencesLast30Days = Math.max(0, Number(referencesLast30Days) || 0);

  const usageStatus = deriveAssetUsageStatus({
    referenceCount,
    referencesLast30Days,
    daysSinceLastReference,
    createdDaysAgo,
  });

  return {
    usageStatus,
    referenceCount,
    referencesLast30Days,
    lastReferencedAt,
    daysSinceLastReference,
    createdDaysAgo,
    usageRuleVersion: metadata.usageRuleVersion || ASSET_USAGE_RULE_VERSION,
  };
}

export function isArchiveCandidate(asset) {
  const referenceCount = Number(asset?.referenceCount ?? asset?.metadata?.referenceCount ?? 0) || 0;
  const daysSinceLastReference = asset?.daysSinceLastReference;
  const createdDaysAgo = Number(asset?.createdDaysAgo ?? 0) || 0;

  if (typeof daysSinceLastReference === 'number') {
    return daysSinceLastReference >= ARCHIVE_CANDIDATE_LAST_REFERENCE_DAYS_THRESHOLD;
  }
  return referenceCount === 0 && createdDaysAgo > ARCHIVE_CANDIDATE_CREATED_DAYS_THRESHOLD;
}
